import { useEffect, useState, ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { MapPin, Video } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { BookingModal } from "@/components/BookingModal";

interface Accompaniment {
  id: string;
  title: string;
  short_description: string;
  full_description: string | null;
  price: number;
  duration_minutes: number;
  image_url: string | null;
  booking_enabled: boolean;
  cabinet_booking_enabled: boolean;
  visio_booking_enabled: boolean;
}

interface Props {
  slug: string;
  trigger: ReactNode;
}

const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const AccompanimentInlineDialog: React.FC<Props> = ({ slug, trigger }) => {
  const [open, setOpen] = useState(false);
  const [accompaniment, setAccompaniment] = useState<Accompaniment | null>(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(false);

  useEffect(() => {
    if (!open || accompaniment) return;
    (async () => {
      const { data } = await supabase
        .from("accompaniments")
        .select("*")
        .eq("is_active", true);
      const match = (data || []).find((a: any) => slugify(a.title) === slug);
      if (match) setAccompaniment(match as Accompaniment);
    })();
  }, [open, slug, accompaniment]);

  const handleBooking = (online: boolean) => {
    setIsOnline(online);
    setBookingOpen(true);
  };

  const service = accompaniment
    ? {
        title: accompaniment.title,
        duration: accompaniment.duration_minutes,
        tarif: `${accompaniment.price}€`,
        description: accompaniment.short_description,
        image: accompaniment.image_url || "",
      }
    : null;

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {accompaniment ? (
            <>
              {accompaniment.image_url && (
                <div className="w-full h-48 overflow-hidden rounded-lg mb-4">
                  <img
                    src={accompaniment.image_url}
                    alt={accompaniment.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <DialogHeader>
                <DialogTitle className="font-cinzel text-primary text-xl">
                  {accompaniment.title}
                </DialogTitle>
                <DialogDescription className="font-montserrat text-muted-foreground">
                  {accompaniment.duration_minutes} min • {accompaniment.price}€
                </DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                {accompaniment.full_description && (
                  <div className="prose prose-sm max-w-none font-montserrat text-foreground whitespace-pre-line">
                    {accompaniment.full_description}
                  </div>
                )}
                {accompaniment.booking_enabled && (
                  <div className="flex flex-col gap-2 pt-4 border-t">
                    {accompaniment.cabinet_booking_enabled && (
                      <Button
                        className="w-full bg-gradient-primary font-montserrat"
                        onClick={() => handleBooking(false)}
                      >
                        <MapPin className="w-4 h-4 mr-2" />
                        Réserver en présentiel
                      </Button>
                    )}
                    {accompaniment.visio_booking_enabled && (
                      <Button
                        variant="outline"
                        className="w-full font-montserrat"
                        onClick={() => handleBooking(true)}
                      >
                        <Video className="w-4 h-4 mr-2" />
                        Réserver en visio
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="py-12 text-center text-muted-foreground font-montserrat">
              Chargement…
            </div>
          )}
        </DialogContent>
      </Dialog>

      <BookingModal
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        service={service}
        isOnline={isOnline}
      />
    </>
  );
};
